import { defineStore } from "pinia";

import type {
  cartType,
  CartItemType,
  StepType,
  StrapiProductType,
  ProductType,
} from "@/types";

interface State {
  showCart: boolean;
  cart: cartType;
  checkoutSteps: StepType[];
  activeStep: number;
  manualAddress: boolean;
  products: ProductType[];
}

const initialCheckoutSteps: StepType[] = [
  {
    slug: "shipping-address",
    title: "Shipping Address",
    completed: false,
  },
  {
    slug: "delivery-method",
    title: "Delivery Method",
    completed: false,
  },
  {
    slug: "payment-method",
    title: "Payment Method",
    completed: false,
  },
  {
    slug: "review",
    title: "Review",
    completed: false,
    submitStep: true,
  },
];

export const useCommerceStore = defineStore({
  id: "commerceStore",
  state: (): State => ({
    showCart: false,
    cart: {
      cart_id:
        Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15),
      orderItems: [],
      total: 0,
      subTotal: 0,
      totalDiscount: 0,
    },
    checkoutSteps: initialCheckoutSteps,
    activeStep: 0,
    manualAddress: false,
    products: [],
  }),
  persist: process.client
    ? ({
        storage: localStorage,
        paths: ["cart", "activeStep"],
      } as any)
    : false,

  actions: {
    toggleMiniCart({ open = false, autoClose = false }) {
      if (this.totalItemsInCart === 0) return;
      if (autoClose) {
        setTimeout(() => {
          this.showCart = false;
        }, 2000);
      }
      this.showCart = open;
    },
    clearCart() {
      this.cart = {
        cart_id:
          Math.random().toString(36).substring(2, 15) +
          Math.random().toString(36).substring(2, 15),
        orderItems: [],
        total: 0,
        subTotal: 0,
        totalDiscount: 0,
      };
    },
    clearCheckoutSteps() {
      this.checkoutSteps = initialCheckoutSteps;
      this.activeStep = 0;
    },
    removeProduct(productCode: string) {
      const itemIndex = this.cart.orderItems.findIndex(
        (el) => el.product.code === productCode
      );
      if (itemIndex === -1) return;
      this.cart.orderItems.splice(itemIndex, 1);
      this.updateCartTotals();
      if (this.cart.orderItems.length === 0) {
        this.showCart = false;
        this.restartCommerce();
      }
    },
    addToCart(item: CartItemType) {
      console.log(this.cart.orderItems);
      const itemIndex = this.cart.orderItems.findIndex(
        (el) => el.product.code === item.product.code
      );
      console.log(itemIndex);
      if (itemIndex === -1) {
        this.cart.orderItems.push(item);
      } else {
        this.cart.orderItems[itemIndex].quantity += item.quantity;
      }

      this.updateCartTotals();
      // this.toggleMiniCart({ open: true, autoClose: true });
    },
    addCartItemQuantity(productCode: string) {
      const itemIndex = this.cart.orderItems.findIndex(
        (el) => el.product.code === productCode
      );
      if (itemIndex === -1) return;
      this.cart.orderItems[itemIndex].quantity++;
      this.updateCartTotals();
    },
    removeCartItemQuantity(productCode: string) {
      const itemIndex = this.cart.orderItems.findIndex(
        (el) => el.product.code === productCode
      );
      if (itemIndex === -1) return;
      if (this.cart.orderItems[itemIndex].quantity === 1) {
        this.removeProduct(productCode);
        return;
      }
      this.cart.orderItems[itemIndex].quantity--;
      this.updateCartTotals();
    },
    updateCartTotals() {
      // Get the same utility functions that Plan/Card.vue uses
      const { getProductPrice, getProductDiscountValue } = formatterUtils();
      
      // Simplified helper function to safely get price for all products including affiliate products
      const getReliablePrice = (product, withDiscount = true) => {
        // Get the recurring prices array
        const recurringPrices = product?.productOfferingPrice?.recurring || [];
        
        // If we have recurring prices, use them
        if (recurringPrices.length > 0) {
          // Get the first recurring price (most products only have one)
          const priceObject = recurringPrices[0];
          
          // If we want to apply discount and it exists
          if (withDiscount && priceObject?.price?.taxIncludedAmountAfterDiscount) {
            return parseFloat(priceObject.price.taxIncludedAmountAfterDiscount) || 0;
          }
          
          // Otherwise use regular price
          if (priceObject?.price?.taxIncludedAmount) {
            return parseFloat(priceObject.price.taxIncludedAmount) || 0;
          }
        }
        
        // If no recurring prices, try oneTime prices
        const oneTimePrices = product?.productOfferingPrice?.oneTime || [];
        if (oneTimePrices.length > 0 && oneTimePrices[0]?.price?.taxIncludedAmount) {
          return parseFloat(oneTimePrices[0].price.taxIncludedAmount) || 0;
        }
        
        // Last resort: try standard method (for backward compatibility)
        const standardPrice = getProductPrice(product, withDiscount);
        if (!isNaN(standardPrice) && standardPrice !== undefined) {
          return standardPrice;
        }
        
        // Ultimate fallback, return 0
        return 0;
      };
      
      // Simplified helper for discount
      const getReliableDiscount = (product) => {
        // Get the recurring prices array
        const recurringPrices = product?.productOfferingPrice?.recurring || [];
        
        // If we have recurring prices, use the discount amount from the first one
        if (recurringPrices.length > 0 && recurringPrices[0]?.price?.discountAmount) {
          return parseFloat(recurringPrices[0].price.discountAmount) || 0;
        }
        
        // Fallback to oneTime prices if available
        const oneTimePrices = product?.productOfferingPrice?.oneTime || [];
        if (oneTimePrices.length > 0 && oneTimePrices[0]?.price?.discountAmount) {
          return parseFloat(oneTimePrices[0].price.discountAmount) || 0;
        }
        
        // Last resort: try standard method
        const standardDiscount = getProductDiscountValue(product);
        if (!isNaN(standardDiscount) && standardDiscount !== undefined) {
          return standardDiscount;
        }
        
        // Ultimate fallback, return 0
        return 0;
      };

      // Calculate totals using our reliable price functions
      this.cart.subTotal = this.cart.orderItems.reduce(
        (acc, el) => acc + getReliablePrice(el?.product, false) * el.quantity,
        0
      );
      this.cart.total = this.cart.orderItems.reduce(
        (acc, el) => acc + getReliablePrice(el?.product, true) * el.quantity,
        0
      );
      this.cart.totalDiscount = this.cart.orderItems.reduce(
        (acc, el) => acc + getReliableDiscount(el?.product) * el.quantity,
        0
      );
      
      // Debug log for troubleshooting
      console.log('Cart updated with totals:', {
        subTotal: this.cart.subTotal,
        total: this.cart.total,
        discount: this.cart.totalDiscount
      });
    },
    restartCommerce() {
      const simpleOrderStore = useSimpleOrderStore();
      this.clearCheckoutSteps();
      this.clearCart();
      this.activeStep = 0;
      simpleOrderStore.clearSimpleOrderPayload();
      this.manualAddress = false;
    },
    updateProducts(products: ProductType[] | null) {
      if (!products) return;
      this.products = products;
    },
    updateProductsWithStrapi(strapiProducts: StrapiProductType[] | null) {
      if (!strapiProducts) return;
      strapiProducts.forEach((strapiProduct) => {
        const productIndex = this.products.findIndex(
          (product) => product.code === strapiProduct.code
        );
        if (productIndex === -1) return;
        this.products[productIndex].strapiData = strapiProduct;
      });
    },
  },
  getters: {
    totalItemsInCart(state) {
      return state.cart.orderItems.length;
    },
    hasDiscountInCart(state) {
      return state.cart.totalDiscount > 0;
    },
  },
});
